/**
 * Enhanced AdminSemesterPage using the new component system
 * Demonstrates semester management with enhanced form and list features
 */

import React, { useEffect, useState } from 'react';
import { FiCalendar, FiCheck, FiClock, FiPlus } from 'react-icons/fi';

// New component system imports
import {
  Badge,
  Checkbox,
  HStack,
  List,
  ListItem,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AdminLayout,
  Button,
  Card,
  FormField,
  FormSection,
  Input,
} from '@grcs/components';

// Existing hooks and types
import { useData } from '@grcs/context/DataContext';
import { useSemesterOperations } from '@grcs/hook/useDatabase';
import { Semester } from '@grcs/types/DataTypes';
import { TABLE_NAMES } from '@grcs/utils/database/types';

interface SemesterStats {
  totalSemesters: number;
  activeSemester: string | null;
  nextYear: string;
}

export const AdminSemesterPageEnhanced: React.FC = () => {
  const { supabase } = useData();
  const semesterOps = useSemesterOperations();
  const toast = useToast();

  const [semesters, setSemesters] = useState<Semester[]>([]);
  const [newSemesterYear, setNewSemesterYear] = useState<string>('');
  const [setAsActive, setSetAsActive] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [stats, setStats] = useState<SemesterStats>({
    totalSemesters: 0,
    activeSemester: null,
    nextYear: new Date().getFullYear().toString(),
  });

  // Load semesters
  useEffect(() => {
    const loadSemesters = async () => {
      try {
        const { data, error } = await semesterOps.getAllSemesters();
        if (error) {
          throw error;
        }

        const sortedSemesters = (data || []).sort(
          (a, b) => parseInt(b.semester_year) - parseInt(a.semester_year),
        );
        setSemesters(sortedSemesters);

        // Calculate stats
        const activeSemester = sortedSemesters.find((s) => s.is_current);
        const currentYear = new Date().getFullYear();
        const latestYear =
          sortedSemesters.length > 0
            ? parseInt(sortedSemesters[0].semester_year)
            : currentYear - 1;

        setStats({
          totalSemesters: sortedSemesters.length,
          activeSemester: activeSemester?.semester_name || null,
          nextYear: (latestYear + 1).toString(),
        });
      } catch (err) {
        console.error('Error loading semesters:', err);
        toast({
          title: 'Error Loading Semesters',
          description: 'Failed to load semester data',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadSemesters();
  }, [semesterOps, toast]);

  // Validation
  const validateSemesterYear = (year: string): string | null => {
    if (!year.trim()) {
      return 'Semester year is required';
    }

    if (!/^\d{4}$/.test(year.trim())) {
      return 'Semester year must be a 4-digit number';
    }

    const yearNum = parseInt(year);
    const currentYear = new Date().getFullYear();

    if (yearNum < 2020 || yearNum > currentYear + 5) {
      return `Year must be between 2020 and ${currentYear + 5}`;
    }

    if (semesters.some((s) => s.semester_year === year)) {
      return 'This semester already exists';
    }

    return null;
  };

  // Event handlers
  const handleAddSemester = async () => {
    const validationError = validateSemesterYear(newSemesterYear);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const yearNum = parseInt(newSemesterYear);

      // If setting as active, first deactivate all existing semesters
      if (setAsActive && semesters.length > 0) {
        const { error: deactivateError } = await supabase
          .from(TABLE_NAMES.SEMESTER)
          .update({ is_current: false });

        if (deactivateError) {
          throw new Error('Failed to deactivate existing semesters');
        }
      }

      const newSemester: Semester = {
        semester_year: newSemesterYear,
        semester_name: `${yearNum}/09 - ${yearNum + 1}/06`,
        is_current: setAsActive,
        deleted: false,
        created_at: new Date().toISOString(),
      };

      const { error: insertError } = await semesterOps.insertSemester(
        newSemester,
      );
      if (insertError) {
        throw insertError;
      }

      // Add to local state
      const updatedSemesters = [newSemester, ...semesters].sort(
        (a, b) => parseInt(b.semester_year) - parseInt(a.semester_year),
      );
      setSemesters(updatedSemesters);

      // Update stats
      setStats((prev) => ({
        ...prev,
        totalSemesters: prev.totalSemesters + 1,
        nextYear: (yearNum + 1).toString(),
      }));

      // If we set this semester as active, update all semesters in local state
      if (setAsActive) {
        const updatedSemestersWithActive = updatedSemesters.map((semester) => ({
          ...semester,
          is_current: semester.semester_year === newSemesterYear,
        }));
        setSemesters(updatedSemestersWithActive);

        // Update stats with new active semester
        setStats((prev) => ({
          ...prev,
          activeSemester: newSemester.semester_name,
          totalSemesters: prev.totalSemesters + 1,
          nextYear: (yearNum + 1).toString(),
        }));
      }

      // Reset form
      setNewSemesterYear('');
      setSetAsActive(false);
      setError('');

      toast({
        title: 'Semester Added',
        description: `Semester ${newSemester.semester_name} has been created${
          setAsActive ? ' and set as active' : ''
        }`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (err: any) {
      console.error('Error adding semester:', err);
      toast({
        title: 'Error Adding Semester',
        description: err.message || 'Failed to create semester',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetActive = async (semesterYear: string) => {
    try {
      // Update all semesters to inactive first
      const { error: deactivateError } = await supabase
        .from(TABLE_NAMES.SEMESTER)
        .update({ is_current: false })
        .neq('semester_year', semesterYear);

      if (deactivateError) throw deactivateError;

      // Set the selected semester as active
      const { error: activateError } = await supabase
        .from(TABLE_NAMES.SEMESTER)
        .update({ is_current: true })
        .eq('semester_year', semesterYear);

      if (activateError) throw activateError;

      // Update local state
      const updatedSemesters = semesters.map((semester) => ({
        ...semester,
        is_current: semester.semester_year === semesterYear,
      }));
      setSemesters(updatedSemesters);

      // Update stats
      const activeSemester = updatedSemesters.find((s) => s.is_current);
      setStats((prev) => ({
        ...prev,
        activeSemester: activeSemester?.semester_name || null,
      }));

      toast({
        title: 'Active Semester Updated',
        description: `${updatedSemesters.find(
          (s) => s.semester_year === semesterYear,
        )?.semester_name} is now active`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (err: any) {
      console.error('Error setting active semester:', err);
      toast({
        title: 'Error Updating Semester',
        description: err.message || 'Failed to set active semester',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <AdminLayout>
      <VStack spacing={8} align="stretch">
        {/* Header Section */}
        <div>
          <Text fontSize="2xl" fontWeight="bold" mb={2}>
            Semester Management
          </Text>
          <Text fontSize="lg" color="gray.600">
            Manage academic semesters and set the active semester
          </Text>
        </div>

        {/* Stats Cards */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <Card padding={6} variant="outline">
            <VStack spacing={2}>
              <HStack>
                <FiCalendar size={20} />
                <Text fontWeight="600">Total Semesters</Text>
              </HStack>
              <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                {stats.totalSemesters}
              </Text>
            </VStack>
          </Card>

          <Card
            padding={6}
            variant={stats.activeSemester ? 'filled' : 'outline'}
          >
            <VStack spacing={2}>
              <HStack>
                <FiCheck size={20} />
                <Text fontWeight="600">Active Semester</Text>
              </HStack>
              <Text fontSize="lg" fontWeight="600" textAlign="center">
                {stats.activeSemester || 'None Selected'}
              </Text>
            </VStack>
          </Card>

          <Card padding={6} variant="subtle">
            <VStack spacing={2}>
              <HStack>
                <FiPlus size={20} />
                <Text fontWeight="600">Suggested Next</Text>
              </HStack>
              <Text fontSize="lg" fontWeight="600" color="green.600">
                {stats.nextYear}
              </Text>
            </VStack>
          </Card>
        </SimpleGrid>

        {/* Semester List */}
        <Card padding={6}>
          <VStack spacing={6} align="stretch">
            <div>
              <Text fontSize="xl" fontWeight="600" mb={2}>
                Existing Semesters
              </Text>
              <Text fontSize="sm" color="gray.600">
                Click "Set Active" to change the current academic semester
              </Text>
            </div>

            {isLoading ? (
              <Text textAlign="center" py={8}>
                Loading semesters...
              </Text>
            ) : semesters.length === 0 ? (
              <Text textAlign="center" py={8} color="gray.500">
                No semesters created yet. Add your first semester below.
              </Text>
            ) : (
              <List spacing={4}>
                {semesters.map((semester) => (
                  <ListItem key={semester.semester_year}>
                    <Card
                      padding={4}
                      variant={semester.is_current ? 'filled' : 'outline'}
                      bg={semester.is_current ? 'green.50' : undefined}
                    >
                      <HStack justify="space-between" align="center">
                        <VStack align="start" spacing={1}>
                          <HStack>
                            <Text fontWeight="600" fontSize="lg">
                              {semester.semester_name}
                            </Text>
                            {semester.is_current && (
                              <Badge colorScheme="green" variant="solid">
                                <HStack spacing={1}>
                                  <FiCheck size={12} />
                                  <span>Active</span>
                                </HStack>
                              </Badge>
                            )}
                          </HStack>
                          <Text fontSize="sm" color="gray.600">
                            Academic Year {semester.semester_year}
                          </Text>
                        </VStack>

                        {!semester.is_current && (
                          <Button
                            size="sm"
                            colorScheme="green"
                            variant="secondary"
                            leftIcon={<FiCheck />}
                            onClick={() =>
                              handleSetActive(semester.semester_year)
                            }
                          >
                            Set Active
                          </Button>
                        )}
                      </HStack>
                    </Card>
                  </ListItem>
                ))}
              </List>
            )}
          </VStack>
        </Card>

        {/* Add New Semester */}
        <Card padding={6} variant="outline">
          <FormSection
            title="Add New Semester"
            description="Create a new academic semester year"
          >
            <VStack spacing={4} align="stretch">
              <HStack spacing={4} align="end">
                <FormField
                  label="Semester Year"
                  required
                  error={error}
                  helperText={`Suggested: ${stats.nextYear}`}
                  flex={1}
                >
                  <Input
                    value={newSemesterYear}
                    onChange={(e) => {
                      setNewSemesterYear(e.target.value);
                      if (error) setError('');
                    }}
                    placeholder="e.g. 2025"
                    leftIcon={<FiCalendar />}
                  />
                </FormField>

                <Button
                  colorScheme="blue"
                  onClick={handleAddSemester}
                  loading={isSubmitting}
                  loadingText="Adding..."
                  leftIcon={<FiPlus />}
                  minW="120px"
                >
                  Add Semester
                </Button>
              </HStack>

              <Checkbox
                isChecked={setAsActive}
                onChange={(e) => setSetAsActive(e.target.checked)}
                colorScheme="green"
              >
                Set as active semester
                {stats.activeSemester && (
                  <Text fontSize="xs" color="gray.600" ml={1}>
                    (will replace "{stats.activeSemester}")
                  </Text>
                )}
              </Checkbox>
            </VStack>

            {newSemesterYear && !error && (
              <Text fontSize="sm" color="blue.600" mt={2}>
                Will create: {parseInt(newSemesterYear)}/09 -{' '}
                {parseInt(newSemesterYear) + 1}/06
                {setAsActive && ' (will be set as active)'}
              </Text>
            )}
          </FormSection>
        </Card>

        {/* Information */}
        <Card padding={6} variant="subtle">
          <VStack spacing={4} align="stretch">
            <HStack>
              <FiClock />
              <Text fontWeight="600">How Semesters Work</Text>
            </HStack>
            <VStack spacing={2} align="start" fontSize="sm">
              <Text>• Only one semester can be active at a time</Text>
              <Text>
                • The active semester determines which classes and data are
                shown by default
              </Text>
              <Text>
                • Semester format: YYYY/MM - YYYY/MM (September to June)
              </Text>
              <Text>
                • Historical data remains accessible even when semesters are not
                active
              </Text>
            </VStack>
          </VStack>
        </Card>
      </VStack>
    </AdminLayout>
  );
};
